'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { Check, ChevronRight, Coins, Gift, Medal, ShoppingBag, Star, Ticket, TrendingUp } from 'lucide-react'
import { Button } from '@/components/elements/Button'
import Progress from '@/components/elements/Progress'
import Skeleton from '@/components/elements/Skeleton'
import { useAuth } from '@/lib/commerce/hooks'
import { getLoyaltyTiersApi, getLoyaltySummaryApi } from '@/lib/commerce/services/loyalty-api'
import { navigateUrl } from '@/utils/client-request'
import { classNames, formatImageURL, formatNumber, formatPrice, getVendorPrimaryColor, isCustomerAuthActive, resolveColorHex } from '@/utils/util'
import { STATUS_LABELS } from '@/utils/order'
import ConvertVoucherModal from './ConvertVoucherModal'

export default function Loyalty({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { session, isLoggedIn } = useAuth()
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))
  const primaryTint = `color-mix(in oklch, ${primary} 10%, white)`

  const [program, setProgram] = useState<any>(null)
  const [loadingProgram, setLoadingProgram] = useState(true)
  const [summary, setSummary] = useState<any>(null)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const tiers = program?.tiers || []
  const ctx = { vendorId: vendor?.id, session: session || undefined }

  useEffect(() => {
    let cancelled = false
    getLoyaltyTiersApi({ vendorId: vendor?.id }).then((programRes: any) => {
      if (cancelled) return
      setProgram(programRes?.data || null)
    }).catch(() => { }).finally(() => { if (!cancelled) setLoadingProgram(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isLoggedIn) return
    let cancelled = false
    setLoadingSummary(true)
    getLoyaltySummaryApi(ctx).then((summaryRes) => {
      if (cancelled) return
      setSummary(summaryRes?.data || null)
    }).catch(() => { }).finally(() => { if (!cancelled) setLoadingSummary(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, refreshKey])

  const benefitLabel = (benefit: any) => {
    if (benefit.type === 'percentDiscount') return `${benefit.valuePercent}% хөнгөлөлт`
    if (benefit.type === 'fixedDiscount') return `${formatPrice(benefit.valueAmount)} хөнгөлөлт`
    if (benefit.type === 'freeShipping') return 'Үнэгүй хүргэлт'
    if (benefit.type === 'shippingDiscount') return `Хүргэлт ${benefit.valuePercent}% хөнгөлөлттэй`
    return 'Нэмэлт урамшуулал'
  }

  const progressPercent = summary?.nextTier
    ? Math.min(100, Math.round((summary.qualifyingSpend / summary.nextTier.minAnnualSpend) * 100))
    : 100

  const steps = [
    { icon: ShoppingBag, t: 'Худалдан авалт бүрээс оноо цуглуул', s: `Захиалга "${STATUS_LABELS[program?.awardStatus] || STATUS_LABELS.paid}" төлөвт орох бүрд түвшнийхээ хувиар оноо орно.` },
    { icon: TrendingUp, t: 'Түвшин ахиж, илүү урамшуулал ав', s: 'Жилийн худалдан авалтаараа дээд түвшинд хүрч, илүү хөнгөлөлт эдлээрэй.' },
    { icon: Ticket, t: 'Оноогоо ваучер болгон ашигла', s: 'Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа хэрэглэнэ.' },
  ]

  return (
    <section className="bg-cream pb-20">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pt-6">
        <nav className="hidden md:block text-[13px] text-foreground/55 mb-6">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Лояалти хөтөлбөр</span>
        </nav>

        <div className="mx-auto max-w-230">
          <div className="bg-white rounded-3xl p-7 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-16 -right-12 w-60 h-60 rounded-full opacity-70 pointer-events-none" style={{ background: primaryTint }} />
            <div className="absolute -top-4 -right-2 w-16 h-16 rounded-full opacity-60 pointer-events-none" style={{ background: primaryTint }} />
            <div className="relative">
              <div className="w-18 h-18 rounded-full inline-flex items-center justify-center text-white" style={{ background: primary }}>
                <Medal className="size-9" strokeWidth={2} />
              </div>
              <h1 className="font-bold font-gilroy tracking-[-0.03em] leading-[0.98] text-foreground mt-5" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)' }}>
                Лояалти хөтөлбөр
              </h1>
              <p className="mt-4 text-[15px] text-foreground/65 max-w-140 leading-relaxed">
                Худалдан авалт бүрээсээ оноо цуглуулж, түвшин ахих тусам илүү их хөнгөлөлт аваарай.
                Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа ашиглах боломжтой.
              </p>
            </div>
          </div>

          {!isLoggedIn && isCustomerAuthActive(vendor) && loadingProgram && (
            <Skeleton className="mt-4 h-36 w-full rounded-3xl" />
          )}

          {!isLoggedIn && isCustomerAuthActive(vendor) && !loadingProgram && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-4 bg-white rounded-3xl p-6 sm:p-7">
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="h-14 w-14 shrink-0 rounded-full inline-flex items-center justify-center text-white" style={{ background: primary }}>
                  <Gift className="size-7" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  {program?.welcomeBonusPoints > 0 ? (
                    <>
                      <div className="font-gilroy text-2xl sm:text-3xl font-bold tracking-[-0.02em]" style={{ color: primary }}>+{formatNumber(program.welcomeBonusPoints)} оноо</div>
                      <p className="mt-1 text-sm text-foreground/65">Бүртгэлээ үүсгэж үнэгүй оноо аваад, ваучер болгон ашиглаарай.</p>
                    </>
                  ) : (
                    <>
                      <div className="font-gilroy text-xl sm:text-2xl font-bold tracking-[-0.02em] text-foreground">Оноо цуглуулж эхлээрэй</div>
                      <p className="mt-1 text-sm text-foreground/65">Бүртгүүлсэн худалдан авагч бүр худалдан авалтаасаа оноо цуглуулна.</p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  <Button tag="a" href={navigateUrl('/register', pathname)} variant="outline" className="h-11 rounded-full! bg-foreground text-cream font-semibold! border-foreground hover:bg-foreground/90 hover:text-cream transition-all">Бүртгүүлэх</Button>
                  <Button tag="a" href={navigateUrl(`/login?redirect=/loyalty`, pathname)} variant="outline" className="h-11 rounded-full! font-semibold!">Нэвтрэх</Button>
                </div>
              </div>
            </motion.div>
          )}

          {isLoggedIn && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-4">
              {loadingSummary && !summary ? (
                <Skeleton className="h-40 w-full rounded-3xl" />
              ) : summary ? (
                <div className="bg-white rounded-3xl p-6 sm:p-7">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {summary.currentTier?.icon ? (
                        <img src={formatImageURL(summary.currentTier.icon)} alt={summary.currentTier.name} className="size-12 rounded-full object-cover border border-foreground/10" />
                      ) : (
                        <div className="size-12 rounded-full bg-[#F1EEE7] inline-flex items-center justify-center"><Medal className="size-6 text-foreground/60" /></div>
                      )}
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/55">Таны түвшин</div>
                        <div className="text-lg font-bold font-gilroy tracking-[-0.015em]">{summary.currentTier?.name || '—'}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/55">Онооны үлдэгдэл</div>
                      <div className="text-[26px] font-bold font-gilroy tracking-[-0.02em] tabular-nums leading-tight">{formatNumber(summary.balance || 0)}</div>
                    </div>
                    <Button icon={Ticket} variant="outline" className="h-11 rounded-full! bg-foreground text-cream font-semibold! border-foreground hover:bg-foreground/90 hover:text-cream transition-all" onClick={() => setConvertOpen(true)} isActive={(summary.balance || 0) >= (program?.minPointsToConvert || 1)}>Ваучер болгох</Button>
                  </div>
                  <div className="mt-5">
                    {summary.nextTier ? (
                      <>
                        <div className="flex items-center justify-between text-[13px] mb-1.5">
                          <span className="text-foreground/55">Энэ жилийн худалдан авалт: <span className="font-semibold text-foreground">{formatPrice(summary.qualifyingSpend || 0)}</span></span>
                          <span className="text-foreground/55">{summary.nextTier.name}: {formatPrice(summary.nextTier.minAnnualSpend)}</span>
                        </div>
                        <Progress value={progressPercent} />
                        <p className="text-[12.5px] text-foreground/55 mt-1.5">{summary.nextTier.name} түвшинд хүрэхэд {formatPrice(summary.nextTier.remainingSpend)} дутуу байна</p>
                      </>
                    ) : (
                      <p className="text-[13.5px] text-foreground/65 flex items-center gap-1.5"><Star className="size-4" style={{ color: primary }} /> Та хамгийн дээд түвшинд байна. Энэ жилийн худалдан авалт {formatPrice(summary.qualifyingSpend || 0)}</p>
                    )}
                  </div>
                  <a href={navigateUrl('/points', pathname)} className="mt-5 text-[13.5px] font-semibold text-foreground inline-flex items-center gap-1 hover:opacity-70 transition-opacity">
                    Онооны түүх үзэх
                    <ChevronRight className="size-4" />
                  </a>
                </div>
              ) : null}
            </motion.div>
          )}

          <div className="mt-4 bg-white rounded-3xl p-6 sm:p-8">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/55">Хэрхэн ажилладаг вэ</div>
            <ul className="mt-6 space-y-6">
              {steps.map((step, index) => {
                const Icon = step.icon
                return (
                  <li key={index} className="flex gap-4">
                    <div className="mt-0.5 size-10 shrink-0 inline-flex items-center justify-center rounded-full bg-[#F1EEE7]" style={{ color: primary }}>
                      <Icon className="size-4.5" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold text-foreground">{step.t}</div>
                      <div className="mt-0.5 text-[13.5px] leading-relaxed text-foreground/60">{step.s}</div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        <div className="mx-auto max-w-5xl mt-12">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/55 text-center">Түвшингүүд</div>
          {loadingProgram && (
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="bg-white rounded-3xl p-6">
                  <div className="flex items-center gap-3">
                    <Skeleton className="size-12 shrink-0 rounded-full" />
                    <div className="flex-1 min-w-0 space-y-2">
                      <Skeleton className="h-5 w-24" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                  <div className="mt-4 space-y-2.5">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loadingProgram && (
          <div className={classNames('mt-5 grid gap-4', tiers.length >= 3 ? 'md:grid-cols-3' : tiers.length === 2 ? 'md:grid-cols-2' : 'max-w-lg mx-auto')}>
            {tiers.map((tier: any, index: number) => {
              const isCurrent = summary?.currentTier?.id === tier.id
              return (
                <motion.div key={tier.id} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} viewport={{ once: true }} className={classNames('bg-white rounded-3xl p-6 flex flex-col border-2', isCurrent ? '' : 'border-transparent')} style={isCurrent ? { borderColor: primary } : undefined}>
                  <div className="flex items-center gap-3">
                    {tier.icon ? (
                      <img src={formatImageURL(tier.icon)} alt={tier.name} className="size-12 rounded-full object-cover border border-foreground/10" />
                    ) : (
                      <div className="size-12 rounded-full bg-[#F1EEE7] inline-flex items-center justify-center"><Medal className="size-6 text-foreground/60" /></div>
                    )}
                    <div>
                      <div className="font-bold font-gilroy text-lg tracking-[-0.015em] flex items-center gap-2">
                        {tier.name}
                        {isCurrent && <span className="text-[10.5px] font-semibold px-2.5 py-0.5 rounded-full text-white tracking-normal" style={{ background: primary }}>Таны түвшин</span>}
                      </div>
                      <div className="text-[13px] text-foreground/55">{tier.minAnnualSpend > 0 ? `${formatPrice(tier.minAnnualSpend)}-с дээш` : 'Эхлэх түвшин'}</div>
                    </div>
                  </div>
                  <ul className="mt-5 space-y-2.5 text-[13.5px] text-foreground/75 flex-1">
                    <li className="flex items-start gap-2.5">
                      <Coins className="size-4 mt-0.5 shrink-0" style={{ color: primary }} />
                      <span>{formatNumber(tier.spendPerPoint)}₮ тутамд {formatNumber(tier.pointsPerUnit)} оноо</span>
                    </li>
                    {tier.reviewRewardPoints > 0 && (
                      <li className="flex items-start gap-2.5">
                        <Star className="size-4 mt-0.5 shrink-0" style={{ color: primary }} />
                        <span>Сэтгэгдэл бүрд {formatNumber(tier.reviewRewardPoints)} оноо</span>
                      </li>
                    )}
                    {(tier.benefits || []).map((benefit: any) => (
                      <li key={benefit.id} className="flex items-start gap-2.5">
                        <Check className="size-4 mt-0.5 shrink-0" style={{ color: primary }} />
                        <span>{benefitLabel(benefit)}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )
            })}
          </div>
          )}
          <p className="text-[12.5px] text-foreground/55 mt-6 text-center">Түвшин жилийн худалдан авалтаар тодорхойлогдож, жил бүр шинээр эхэлнэ. Цуглуулсан оноо хадгалагдана.</p>
        </div>

      </div>

      <ConvertVoucherModal open={convertOpen} setOpen={setConvertOpen} vendor={vendor} program={program} balance={summary?.balance || 0} onConverted={() => setRefreshKey((key) => key + 1)} />
    </section>
  )
}
